export const WEB_LOCALES = ["fr", "mg", "en"];
export const WEB_LOCALE_STORAGE_KEY = "kajikajy-locale";
export const WEB_SITE_PATHS = {
    fr: {
        home: "/",
        about: "/a-propos",
    },
    mg: {
        home: "/mg",
        about: "/mg/a-propos",
    },
    en: {
        home: "/en",
        about: "/en/a-propos",
    },
};
export function isWebLocale(value) {
    return typeof value === "string" && WEB_LOCALES.includes(value);
}
export function resolveWebLocale(value, fallback = "fr") {
    return isWebLocale(value) ? value : fallback;
}
const localeCookieMaxAgeSeconds = 60 * 60 * 24 * 365;
export function readSharedWebLocale() {
    if (typeof document === "undefined") {
        return null;
    }
    const prefix = `${WEB_LOCALE_STORAGE_KEY}=`;
    const value = document.cookie
        .split(";")
        .map((entry) => entry.trim())
        .find((entry) => entry.startsWith(prefix))
        ?.slice(prefix.length) ?? null;
    return isWebLocale(value) ? value : null;
}
export function getInitialWebLocale(fallback = "fr") {
    if (typeof window === "undefined") {
        return fallback;
    }
    const sharedLocale = readSharedWebLocale();
    if (sharedLocale) {
        return sharedLocale;
    }
    try {
        return resolveWebLocale(window.localStorage.getItem(WEB_LOCALE_STORAGE_KEY), fallback);
    }
    catch {
        return fallback;
    }
}
export function persistWebLocale(locale) {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return;
    }
    try {
        window.localStorage.setItem(WEB_LOCALE_STORAGE_KEY, locale);
    }
    catch {
        // The shared cookie remains the source of truth when storage is unavailable.
    }
    const hostname = window.location.hostname;
    const domain = hostname === "kajikajy.com" || hostname.endsWith(".kajikajy.com")
        ? "; Domain=.kajikajy.com"
        : "";
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
        `${WEB_LOCALE_STORAGE_KEY}=${locale}; Path=/; Max-Age=${localeCookieMaxAgeSeconds}; SameSite=Lax` +
            `${domain}${secure}`;
}
export const WEB_I18N = {
    fr: {
        languageCode: "FR",
        languageName: "Français",
        common: {
            plans: "Plans",
            account: "Mon compte",
            subscribe: "Souscrire",
            and: "et",
            language: "Langue",
            darkMode: "Activer le mode sombre",
            lightMode: "Activer le mode clair",
        },
        plans: {
            essor: {
                priceMonthly: "12 990 Ar/mois",
                headline: "Le plan pour enlever les plafonds de la version gratuite : clients, fournisseurs, transactions et articles de stock.",
                features: [
                    "Clients et fournisseurs sans plafond",
                    "Transactions et stock sans plafond",
                    "Ventes, achats, paiements partiels et obligations",
                    "Statistiques, PDF et suivi des performances",
                    "Sauvegarde cloud avec restauration assistée",
                ],
            },
            excellence: {
                priceMonthly: "26 990 Ar/mois",
                headline: "Le plan pour travailler sur plusieurs appareils et garder la restauration cloud incluse.",
                features: [
                    "Tout ce qu'Essor inclut",
                    "Compte utilisable sur 3 appareils en simultanée",
                    "Restauration cloud incluse",
                    "Continuité de travail entre les appareils",
                    "Priorité sur les opérations sensibles",
                ],
            },
        },
        site: {
            metaDescription: "Kajikajy est une application mobile de gestion destinée aux petits et micro-entrepreneurs, commerçants et artisans. Elle aide à suivre les ventes, obligations, stocks, clients, fournisseurs, emprunts et immobilisations.",
            title: "Kajikajy | Le carnet de comptes des entrepreneurs",
            heroStatement: "Une saisie. Tout à jour.",
            lead: "Enregistrez une vente, un achat ou un mouvement de stock. Kajikajy propage l'information aux bons endroits, automatiquement et sans ressaisie.",
            primaryCta: "Créer mon compte",
            secondaryCta: "Voir les plans",
            accountCta: "Mon compte",
            playStoreBadgeAria: "Télécharger l'application Kajikajy sur Google Play",
            playStoreBadgeTop: "Télécharger sur",
            playStoreBadgeBrand: "Google Play",
            playStoreHint: "Kajikajy est disponible dès maintenant sur Google Play.",
            operationsEyebrow: "Comment ça fonctionne",
            operationsTitle: "Chaque opération propage l'information où elle doit aller.",
            operationsIntro: "Six types d'opérations couvrent l'essentiel d'une activité. Chacune met à jour exactement les écrans concernés, et rien d'autre.",
            operations: {
                cashSale: {
                    name: "Vente comptant",
                    outputs: ["Caisse", "Vente du jour", "Statistiques"],
                },
                creditSale: {
                    name: "Vente à crédit",
                    outputs: ["Fiche client", "Reste à encaisser", "Échéance"],
                },
                partialPayment: {
                    name: "Paiement partiel",
                    outputs: ["Solde client", "Solde fournisseur"],
                },
                supplierPurchase: {
                    name: "Achat fournisseur",
                    outputs: ["Stock", "Charge", "Dette fournisseur"],
                },
                stockMovement: {
                    name: "Mouvement de stock",
                    outputs: ["Quantité", "Unité de vente", "Seuil d'alerte"],
                },
                obligation: {
                    name: "Obligations",
                    outputs: ["Salaire", "Publicité", "Mensualités"],
                },
            },
            intelligenceEyebrow: "Ce que ça produit",
            intelligenceTitle: "Vos données travaillent à votre place.",
            intelligence: {
                statistics: {
                    title: "Statistiques en temps réel",
                    text: "Les chiffres de caisse, les ventes du jour et les marges par période se calculent à partir des opérations déjà enregistrées. Aucune ressaisie.",
                },
                pdf: {
                    title: "Export PDF à la demande",
                    text: "Récapitulatif de période, suivi des performances, historique de stock : les documents s'exportent depuis les données existantes, sans reformatage.",
                },
                accounting: {
                    title: "Données comptables",
                    text: "Compte de résultat, bilan, flux de trésorerie et journal de caisse sont générées automatiquement sous PDF ou Excel à partir des données enregistrées.",
                },
                backup: {
                    title: "Sauvegarde et restauration",
                    text: "La sauvegarde cloud s'exécute en continu. En cas de panne ou de changement d'appareil, la restauration remet tout en état sans perte.",
                },
            },
            plansTitle: "Choisissez Essor ou Excellence",
            plansText: "Accessible et simple d'utilisation, Kajikajy est le meilleur outil de gestion des entrepreneurs de Madagascar. Commencez gratuitement et téléchargez l'application sur Google Play, puis choisissez Essor ou Excellence quand votre activité en a besoin.",
            planCta: {
                essor: "Choisir Essor",
                excellence: "Choisir Excellence",
            },
            closingTitle: "Passez à Essor ou Excellence dès aujourd’hui",
            closingText: "Gérez votre activité sans plafond avec Essor, ou profitez en plus de la continuité entre plusieurs appareils avec Excellence.",
            closingCta: "Créer mon compte",
            about: "À propos",
            legalPrivacy: "Confidentialité web",
            legalAppPrivacy: "Confidentialité App",
            legalTerms: "Conditions",
            legalNotice: "Mentions légales",
            legalDeletion: "Suppression du compte",
            copyright: "Copyright © 2026 Kajikajy. Tous droits réservés.",
            footer: "Kajikajy.",
        },
        subscribe: {
            accountLink: "Mon compte",
            title: "Passez à Essor ou Excellence",
            lead: "Sélectionnez l'offre, indiquez l'activité à rattacher et le numéro Mobile Money qui servira au règlement. Kajikajy prépare ensuite la facture d'abonnement avec le bon montant.",
            trustLabel: "Repères",
            trustAccount: "Accès 30 jours",
            trustPayment: "Paiement Mobile Money",
            trustActivation: "Facture Kajikajy",
            plansLabel: "Choix de l'offre",
            legalLinksLabel: "Liens du site",
            invoicePanelLabel: "Récapitulatif de souscription",
            invoiceTitle: "Facture d'abonnement",
            activityLabel: "Activité à rattacher",
            activityPlaceholder: "Ex. Boutique Rabe",
            emailLabel: "Email de réception (optionnel)",
            emailPlaceholder: "nom@exemple.com",
            phoneLabel: "Numéro Mobile Money",
            phonePlaceholder: "38 00 000 00",
            phoneCountryLabel: "Indicatif pays",
            phoneCountrySearch: "Rechercher un pays ou un code",
            phoneCountryNoResults: "Aucun pays trouvé.",
            pinCreateLabel: "Créer un code PIN",
            pinConfirmLabel: "Confirmer le code",
            pinPlaceholder: "4 chiffres",
            selectedOfferLabel: "Offre",
            periodLabel: "Période d'accès",
            periodValue: "30 jours",
            amountLabel: "À payer",
            paymentMethodLabel: "Règlement",
            paymentMethodValue: "Mobile Money",
            note: "L'abonnement est activé après confirmation du paiement.",
            invoiceReadyTitle: "Paiement prêt",
            invoiceReadyText: "Le paiement est prêt. Vérifiez le montant, puis ouvrez la fenêtre sécurisée.",
            invoiceReferenceLabel: "Référence paiement",
            creatingInvoice: "Préparation de la facture",
            creatingPayment: "Préparation du paiement",
            paymentDialogTitle: "Paiement",
            paymentDialogText: "Vérifiez le montant puis lancez le paiement Mobile Money.",
            paymentPreparingText: "Connexion sécurisée à Papi. Cette étape peut prendre quelques secondes.",
            paymentOpeningTitle: "Fenêtre de paiement ouverte",
            paymentOpeningText: "Finalisez le paiement dans la fenêtre Papi. Cette fenêtre Kajikajy restera ouverte.",
            paymentVerifyingTitle: "Vérification du paiement",
            paymentVerifyingText: "Le retour de Papi a été reçu. Kajikajy attend la confirmation sécurisée du serveur.",
            paymentInterruptedTitle: "Paiement interrompu",
            paymentInterruptedText: "Aucun paiement n'est confirmé pour le moment. Vous pouvez reprendre ce paiement ou vérifier son statut.",
            paymentDelayedTitle: "Confirmation en attente",
            paymentDelayedText: "Papi n'a pas encore confirmé le résultat. Aucun échec n'est déclaré ; vous pouvez vérifier à nouveau.",
            paymentSuccessTitle: "Paiement réussi !",
            paymentSuccessText: "Le paiement est confirmé et l'abonnement Kajikajy est activé.",
            paymentFailureTitle: "Paiement échoué",
            paymentFailureText: "Le serveur a confirmé que le paiement n'a pas abouti. Vous pouvez réessayer.",
            paymentPopupBlockedTitle: "Fenêtre de paiement bloquée",
            paymentPopupBlockedText: "Votre navigateur a bloqué la fenêtre Papi. Autorisez les fenêtres contextuelles pour Kajikajy, puis réessayez.",
            reopenPaymentButton: "Rouvrir le paiement",
            continueSameTabButton: "Continuer dans cet onglet",
            paymentStorageUnavailableText: "Le stockage de session est indisponible. Pour conserver le suivi sécurisé, réessayez dans la fenêtre Papi.",
            checkAgainButton: "Vérifier maintenant",
            goToAccountButton: "Accéder à mon compte",
            paymentProviderLabel: "Prestataire",
            paymentUnavailableText: "Le paiement n'est pas disponible dans cet environnement.",
            retryButton: "Réessayer",
            closeButton: "Fermer",
            validationBusiness: "Indiquez l'activité à rattacher.",
            validationEmail: "Indiquez un email valide.",
            validationPhone: "Indiquez un numéro Mobile Money valide.",
            validationPin: "Créez un code PIN à 4 chiffres.",
            validationPinMismatch: "Les deux codes PIN ne correspondent pas.",
            firebaseMissing: "La configuration Firebase web est absente.",
            invoiceError: "La facture n'a pas pu être préparée.",
            continueButton: "Payer",
        },
        account: {
            eyebrow: "Mon compte",
            title: "Consulter son abonnement Kajikajy",
            lead: "Le plan actif, la date d'expiration et les actions de renouvellement seront affichés ici dès l'activation de l'accès.",
            signinTitle: "Connexion sécurisée",
            signinText: "L'accès sera ouvert avec le parcours final d'abonnement. Les actions restent verrouillées tant que le paiement n'est pas activé.",
            signinButton: "Se connecter",
            statusGridLabel: "Aperçu abonnement",
            statusTitle: "Statut",
            statusText: "État de l'abonnement en un coup d'œil",
            plansTitle: "Plans",
            renewalTitle: "Renouvellement",
            renewalText: "Renouvellement et mise à niveau prévus depuis cet espace",
        },
    },
    mg: {
        languageCode: "MG",
        languageName: "Malagasy",
        common: {
            plans: "Tolotra",
            account: "Ny kaontiko",
            subscribe: "Hisoratra anarana",
            and: "sy",
            language: "Fiteny",
            darkMode: "Alefaso ny endrika maizina",
            lightMode: "Alefaso ny endrika mazava",
        },
        plans: {
            essor: {
                priceMonthly: "12 990 Ar/volana",
                headline: "Ampiasao tsy misy fetra ireo fitaovana rehetra ilaina amin’ny fitantanana isan’andro, miaraka amin’ny backup cloud sy fanampiana amin’ny famerenana angona.",
                features: [
                    "Mpanjifa sy mpamatsy tsy misy fetra",
                    "Raharaham-barotra sy entana anaty tahiry tsy misy fetra",
                    "Varotra, fividianana, fandoavana ampahany ary adidy ara-bola",
                    "Antontan’isa, rakitra PDF ary fanaraha-maso ny fivoaran’ny asa",
                    "Fitehirizana ao amin’ny cloud, miaraka amin’ny fanampiana rehefa mamerina angona",
                ],
            },
            excellence: {
                priceMonthly: "26 990 Ar/volana",
                headline: "Ampiasao amin’ny fitaovana maromaro i Kajikajy, ary tafiditra ao ny famerenana angona avy amin’ny cloud.",
                features: [
                    "Tafiditra avokoa izay rehetra omen’ny Essor",
                    "Kaonty iray azo ampiasaina amin’ny fitaovana 3 miaraka",
                    "Tafiditra ny famerenana angona avy amin’ny cloud",
                    "Afaka manohy ny asa amin’ny fitaovana hafa",
                    "Laharam-pahamehana amin’ny fikirakirana raharaha saro-pady",
                ],
            },
        },
        site: {
            metaDescription: "Kajikajy dia rindranasa fitantanana amin’ny finday, natao ho an’ny mpandraharaha madinika, ny mpivarotra ary ny mpanao asa-tanana. Ahafahanao manara-maso ny varotra, ny vola tokony horaisina sy haloana, ny tahiry, ny mpanjifa, ny mpamatsy, ny findramam-bola ary ny fananana raikitra.",
            title: "Kajikajy | Fitaovana fitantanana ho an’ny mpandraharaha",
            heroStatement: "Ampidiro indray mandeha. Havaozina ho azy ny ambiny.",
            lead: "Raketo ny varotra, ny fividianana na ny fivezivezen’ny entana. Kajikajy no manavao ho azy ny toerana rehetra voakasika, ka tsy mila mampiditra izany fanindroany ianao.",
            primaryCta: "Hamorona kaonty",
            secondaryCta: "Hijery ny tolotra",
            accountCta: "Ny kaontiko",
            playStoreBadgeAria: "Sintomy ao amin’ny Google Play i Kajikajy",
            playStoreBadgeTop: "Alaivo ao amin’ny",
            playStoreBadgeBrand: "Google Play",
            playStoreHint: "Azo alaina ao amin’ny Google Play izao i Kajikajy.",
            operationsEyebrow: "Fomba fiasan’i Kajikajy",
            operationsTitle: "Isaky ny mampiditra raharaha iray ianao, havaozin’i Kajikajy avy hatrany izay rehetra voakasika.",
            operationsIntro: "Karazana raharaha enina no mandrakotra ny ankamaroan’ny fitantanana andavanandro. Isaky ny mampiditra iray ianao, ireo pejy tena voakasika ihany no havaozin’i Kajikajy.",
            operations: {
                cashSale: {
                    name: "Varotra voaloa avy hatrany",
                    outputs: ["Vola an-tanana", "Varotra androany", "Antontan'isa"],
                },
                creditSale: {
                    name: "Varotra atao trosa",
                    outputs: [
                        "Kaontin’ny mpanjifa",
                        "Vola mbola haloa",
                        "Daty handoavana",
                    ],
                },
                partialPayment: {
                    name: "Fandoavana ampahany",
                    outputs: [
                        "Ambin’ny trosan’ny mpanjifa",
                        "Ambin’ny trosa amin’ny mpamatsy",
                    ],
                },
                supplierPurchase: {
                    name: "Fividianana amin’ny mpamatsy",
                    outputs: ["Tahiry entana", "Fandaniana", "Trosa amin’ny mpamatsy"],
                },
                stockMovement: {
                    name: "Fidirana sy fivoahan’ny entana",
                    outputs: [
                        "Isan’ny entana",
                        "Fatra ivarotana",
                        "Fetra ambany fampitandremana",
                    ],
                },
                obligation: {
                    name: "Adidy ara-bola",
                    outputs: ["Karama", "Dokam-barotra", "Vola aloa isam-bolana"],
                },
            },
            intelligenceEyebrow: "Ny vokatra azonao",
            intelligenceTitle: "Ny angonao no manampy anao hitantana tsara ny asanao.",
            intelligence: {
                statistics: {
                    title: "Antontan’isa havaozina avy hatrany",
                    text: "Kajian’i Kajikajy avy amin’ny angona efa nampidirinao ny vola an-tanana, ny varotra androany ary ny tombom-barotra isaky ny vanim-potoana. Tsy mila mampiditra zavatra indroa ianao.",
                },
                pdf: {
                    title: "Tatitra PDF vonona rehefa ilainao",
                    text: "Na famintinana isaky ny vanim-potoana, na fanaraha-maso ny fivoaran’ny asa, na tantaran’ny tahiry: avy amin’ny angona efa ao amin’i Kajikajy no anaovana ireo antontan-taratasy, ka tsy mila mandamina azy indray ianao.",
                },
                accounting: {
                    title: "Tatitra ara-kaonty",
                    text: "Kajikajy no mamorona ho azy ny compte de résultat, bilan, flux de trésorerie ary journal de caisse avy amin’ny angonao. Azonao alaina amin’ny endrika PDF na Excel ireo.",
                },
                backup: {
                    title: "Fiarovana sy famerenana ny angona",
                    text: "Tehirizin’i Kajikajy tsy tapaka ao amin’ny cloud ny angonao. Raha simba na soloina ny fitaovana ampiasainao, dia averina amin’ny laoniny ny angonao ka tsy very ny asa efa vita.",
                },
            },
            plansTitle: "Safidio izay mety aminao: Essor na Excellence",
            plansText: "Mora vidy sady tena mora ampiasaina i Kajikajy, ka izy no fitaovana fitantanana tsara indrindra ho an’ny mpandraharaha eto Madagasikara. Alaivo ao amin’ny Google Play ny rindranasa dia ampiasao maimaim-poana aloha. Rehefa mitombo ny filàn’ny asanao, safidio ny Essor na Excellence.",
            planCta: {
                essor: "Safidio ny Essor",
                excellence: "Safidio ny Excellence",
            },
            closingTitle: "Safidio dieny anio izay mety amin’ny asanao",
            closingText: "Misafidiana ny tolotra Essor raha te hampiasa tsy misy fetra an'i Kajikajy ianao. Safidio kosa ny tolotra Excellence raha mila mampiasa Kajikajy amin’ny fitaovana maromaro.",
            closingCta: "Hamorona kaonty",
            about: "Momba anay",
            legalPrivacy: "Tsiambaratelon’ny tranonkala",
            legalAppPrivacy: "Tsiambaratelon’ny rindranasa",
            legalTerms: "Fepetra fampiasana",
            legalNotice: "Fanamarihana ara-dalàna",
            legalDeletion: "Fanafoanana ny kaonty",
            copyright: "Copyright © 2026 Kajikajy. Zo rehetra voatokana.",
            footer: "Kajikajy.",
        },
        subscribe: {
            accountLink: "Ny kaontiko",
            title: "Safidio izay mety aminao: Essor na Excellence",
            lead: "Safidio ny tolotra mety aminao, dia ampidiro ny anaran’ny asanao sy ny laharana Mobile Money handoavana. Homanin’i Kajikajy avy hatrany ny faktiora mifanaraka amin’ny tolotra nosafidinao.",
            trustLabel: "Antsipirian’ny tolotra",
            trustAccount: "Fampiasana mandritra ny 30 andro",
            trustPayment: "Fandoavana amin’ny Mobile Money",
            trustActivation: "Faktiora avy amin’i Kajikajy",
            plansLabel: "Tolotra azo isafidianana",
            legalLinksLabel: "Rohy ato amin’ny tranonkala",
            invoicePanelLabel: "Famintinana ny tolotra nofidinao",
            invoiceTitle: "Faktioran’ny tolotra",
            activityLabel: "Anaran’ny asanao",
            activityPlaceholder: "Ohatra: Boutique Rabe",
            emailLabel: "Email handraisana ny faktiora (tsy voatery)",
            emailPlaceholder: "anarana@ohatra.com",
            phoneLabel: "Laharana Mobile Money",
            phonePlaceholder: "38 00 000 00",
            phoneCountryLabel: "Kaodin’ny firenena",
            phoneCountrySearch: "Karohy ny firenena na ny kaody",
            phoneCountryNoResults: "Tsy nahitana firenena.",
            pinCreateLabel: "Mamoròna kaody PIN",
            pinConfirmLabel: "Avereno ilay kaody",
            pinPlaceholder: "Isa 4",
            selectedOfferLabel: "Tolotra nofidina",
            periodLabel: "Faharetan’ny tolotra",
            periodValue: "30 andro",
            amountLabel: "Vola aloa",
            paymentMethodLabel: "Fomba fandoavana",
            paymentMethodValue: "Mobile Money",
            note: "Rehefa voamarina ny fandoavana vao manomboka ny tolotra.",
            invoiceReadyTitle: "Afaka mandoa ianao izao",
            invoiceReadyText: "Hamarino ny vola tokony haloanao, dia tohizo ao amin’ny pejy azo antoka ny fandoavana.",
            invoiceReferenceLabel: "Laharana famantarana ny fandoavana",
            creatingInvoice: "Manomana ny faktiora…",
            creatingPayment: "Manomana ny fandoavana…",
            paymentDialogTitle: "Fandoavana",
            paymentDialogText: "Hamarino ny vola tokony haloanao, dia tohizo amin’ny Mobile Money ny fandoavana.",
            paymentPreparingText: "Eo am-panomanana ny fandoavana amin’ny fomba azo antoka. Mety haharitra segondra vitsy izany.",
            paymentOpeningTitle: "Nisokatra ny pejy fandoavana",
            paymentOpeningText: "Tohizo ao amin’ny pejy Papi vao nisokatra ny fandoavana. Avelao hisokatra ity pejy Kajikajy ity mandra-pahavitan’izany.",
            paymentVerifyingTitle: "Eo am-panamarinana ny fandoavana",
            paymentVerifyingText: "Voarain’i Kajikajy ny valin’ny pejy Papi ary miandry ny fanamafisana farany avy amin’ny rafitra.",
            paymentInterruptedTitle: "Najanona ny fandoavana",
            paymentInterruptedText: "Mbola tsy voamarina ny fandoavanao. Azonao tohizana ilay fandoavana na hamarinina raha efa tontosa izany.",
            paymentDelayedTitle: "Mbola miandry fanamafisana",
            paymentDelayedText: "Tsy mbola voaray ny fanamafisana farany momba ny fandoavanao. Tsy midika izany hoe tsy nahomby izy; azonao hamarinina indray.",
            paymentSuccessTitle: "Vita soa aman-tsara ny fandoavana!",
            paymentSuccessText: "Voamarina ny fandoavanao ary efa afaka ampiasaina ny tolotra Kajikajy nosafidinao.",
            paymentFailureTitle: "Tsy tontosa ny fandoavana",
            paymentFailureText: "Voamarina fa tsy tontosa ny fandoavanao. Azonao atao ny manandrana indray.",
            paymentPopupBlockedTitle: "Tsy nisokatra ny pejy fandoavana",
            paymentPopupBlockedText: "Nosakanan’ny navigateur ny pejy Papi. Omeo alalana hisokatra ny pop-up ho an’i Kajikajy, dia andramo indray.",
            reopenPaymentButton: "Sokafy indray ny pejy fandoavana",
            continueSameTabButton: "Tohizo eto amin’ity pejy ity",
            paymentStorageUnavailableText: "Tsy afaka mitahiry vonjimaika ny mombamomba ity fandoavana ity ny navigateur. Mba hitohizan’ny fanaraha-maso azy dia sokafy indray ny pejy Papi.",
            checkAgainButton: "Hamarino indray",
            goToAccountButton: "Hankao amin’ny kaontiko",
            paymentProviderLabel: "Mpikirakira ny fandoavana",
            paymentUnavailableText: "Tsy afaka mandoa eto ianao amin’izao fotoana izao.",
            retryButton: "Andramo indray",
            closeButton: "Akatona",
            validationBusiness: "Ampidiro ny anaran’ny asanao.",
            validationEmail: "Ampidiro ny adiresy email marina.",
            validationPhone: "Ampidiro ny laharana Mobile Money marina.",
            validationPin: "Mamoròna kaody PIN misy isa 4.",
            validationPinMismatch: "Tsy mitovy ireo kaody PIN nampidirinao.",
            firebaseMissing: "Tsy mandeha ny rafitra fandoavana amin’izao fotoana izao.",
            invoiceError: "Tsy voaomana ny faktiora. Andramo indray afaka fotoana fohy.",
            continueButton: "Handoa izao",
        },
        account: {
            eyebrow: "Ny kaontiko",
            title: "Hijery ny famandrihana Kajikajy",
            lead: "Haseho eto ny plan mandeha, daty fiafarana ary hetsika fanavaozana rehefa mandeha ny fidirana.",
            signinTitle: "Fidirana voaaro",
            signinText: "Hisokatra miaraka amin'ny lalana farany amin'ny famandrihana ny fidirana. Mbola mihidy ny hetsika raha tsy mandeha ny fandoavana.",
            signinButton: "Hiditra",
            statusGridLabel: "Topimaso famandrihana",
            statusTitle: "Sata",
            statusText: "Satan'ny famandrihana amin'ny fijery iray",
            plansTitle: "Tolotra",
            renewalTitle: "Fanavaozana",
            renewalText: "Fanavaozana sy fanatsarana voaomana ato amin'ity efitra ity",
        },
    },
    en: {
        languageCode: "EN",
        languageName: "English",
        common: {
            plans: "Plans",
            account: "My account",
            subscribe: "Subscribe",
            and: "and",
            language: "Language",
            darkMode: "Enable dark mode",
            lightMode: "Enable light mode",
        },
        plans: {
            essor: {
                priceMonthly: "12 990 Ar/month",
                headline: "The plan that removes free-version limits on customers, suppliers, transactions and stock items.",
                features: [
                    "Customers and suppliers without caps",
                    "Transactions and stock without caps",
                    "Sales, purchases, partial payments and obligations",
                    "Statistics, PDFs and performance tracking",
                    "Cloud backup with assisted recovery",
                ],
            },
            excellence: {
                priceMonthly: "26 990 Ar/month",
                headline: "The plan for working across several devices with cloud recovery included.",
                features: [
                    "Everything Essor includes",
                    "Account usable on 3 devices simultaneously",
                    "Cloud recovery included",
                    "Work continuity between devices",
                    "Priority for sensitive operations",
                ],
            },
        },
        site: {
            metaDescription: "Kajikajy is a mobile management app designed for small and micro-entrepreneurs, traders, and craftspeople. It helps track sales, obligations, stock, customers, suppliers, loans, and fixed assets.",
            title: "Kajikajy | The account book for entrepreneurs",
            heroStatement: "One entry. Everything updated.",
            lead: "Record a sale, a purchase or a stock movement. Kajikajy sends the information where it belongs, automatically and without entering it twice.",
            primaryCta: "Create my account",
            secondaryCta: "View plans",
            accountCta: "My account",
            playStoreBadgeAria: "Download the Kajikajy app on Google Play",
            playStoreBadgeTop: "Get it on",
            playStoreBadgeBrand: "Google Play",
            playStoreHint: "Kajikajy is now available on Google Play.",
            operationsEyebrow: "How it works",
            operationsTitle: "Each operation sends information where it belongs.",
            operationsIntro: "Six operation types cover the essentials of a business. Each one updates exactly the relevant screens, and nothing else.",
            operations: {
                cashSale: {
                    name: "Cash sale",
                    outputs: ["Cash", "Daily sales", "Statistics"],
                },
                creditSale: {
                    name: "Credit sale",
                    outputs: ["Customer file", "Remaining amount", "Due date"],
                },
                partialPayment: {
                    name: "Partial payment",
                    outputs: ["Customer balance", "Supplier balance"],
                },
                supplierPurchase: {
                    name: "Supplier purchase",
                    outputs: ["Stock", "Expense", "Supplier debt"],
                },
                stockMovement: {
                    name: "Stock movement",
                    outputs: ["Quantity", "Sales unit", "Alert threshold"],
                },
                obligation: {
                    name: "Obligations",
                    outputs: ["Salaries", "Advertising", "Instalments"],
                },
            },
            intelligenceEyebrow: "What it produces",
            intelligenceTitle: "Your data works for you.",
            intelligence: {
                statistics: {
                    title: "Real-time statistics",
                    text: "Cash figures, daily sales and margins by period are calculated from operations already recorded. No duplicate entry.",
                },
                pdf: {
                    title: "PDF export on demand",
                    text: "Period summary, performance tracking, stock history: documents export from existing data, without reformatting.",
                },
                accounting: {
                    title: "Accounting data",
                    text: "Income statement, balance sheet, cash flow and cash book are generated automatically as PDF or Excel files from recorded data.",
                },
                backup: {
                    title: "Backup and recovery",
                    text: "Cloud backup runs continuously. If a device fails or changes, recovery puts everything back without loss.",
                },
            },
            plansTitle: "Choose Essor or Excellence",
            plansText: "Accessible and easy to use, Kajikajy is the best management tool for entrepreneurs in Madagascar. Start for free, then choose Essor or Excellence when your activity needs it.",
            planCta: {
                essor: "Choose Essor",
                excellence: "Choose Excellence",
            },
            closingTitle: "Choose the plan that fits your business today",
            closingText: "Choose the Essor plan for unlimited use of Kajikajy. Choose the Excellence plan if you need to use Kajikajy on multiple devices.",
            closingCta: "Create my account",
            about: "About",
            legalPrivacy: "Web privacy",
            legalAppPrivacy: "App privacy",
            legalTerms: "Terms",
            legalNotice: "Legal notice",
            legalDeletion: "Account deletion",
            copyright: "Copyright © 2026 Kajikajy. All rights reserved.",
            footer: "Kajikajy.",
        },
        subscribe: {
            accountLink: "My account",
            title: "Move to Essor or Excellence",
            lead: "Choose a plan, enter the business it belongs to and the Mobile Money number used for payment. Kajikajy then prepares the subscription invoice with the correct amount.",
            trustLabel: "Details",
            trustAccount: "30-day access",
            trustPayment: "Mobile Money payment",
            trustActivation: "Kajikajy invoice",
            plansLabel: "Offer choice",
            legalLinksLabel: "Site links",
            invoicePanelLabel: "Subscription summary",
            invoiceTitle: "Subscription invoice",
            activityLabel: "Business to attach",
            activityPlaceholder: "Example Rabe Shop",
            emailLabel: "Receipt email (optional)",
            emailPlaceholder: "name@example.com",
            phoneLabel: "Mobile Money number",
            phonePlaceholder: "38 00 000 00",
            phoneCountryLabel: "Country code",
            phoneCountrySearch: "Search country or code",
            phoneCountryNoResults: "No country found.",
            pinCreateLabel: "Create a PIN",
            pinConfirmLabel: "Confirm the PIN",
            pinPlaceholder: "4 digits",
            selectedOfferLabel: "Offer",
            periodLabel: "Access period",
            periodValue: "30 days",
            amountLabel: "Amount due",
            paymentMethodLabel: "Settlement",
            paymentMethodValue: "Mobile Money",
            note: "The subscription is activated after payment confirmation.",
            invoiceReadyTitle: "Payment ready",
            invoiceReadyText: "Payment is ready. Check the amount, then open the secure window.",
            invoiceReferenceLabel: "Payment reference",
            creatingInvoice: "Preparing invoice",
            creatingPayment: "Preparing payment",
            paymentDialogTitle: "Payment",
            paymentDialogText: "Check the amount, then start the Mobile Money payment.",
            paymentPreparingText: "Connecting securely to Papi. This may take a few seconds.",
            paymentOpeningTitle: "Payment window opened",
            paymentOpeningText: "Complete the payment in the Papi window. This Kajikajy window will remain open.",
            paymentVerifyingTitle: "Verifying payment",
            paymentVerifyingText: "Papi has returned. Kajikajy is waiting for secure server confirmation.",
            paymentInterruptedTitle: "Payment interrupted",
            paymentInterruptedText: "No payment is confirmed yet. You can resume this payment or check its status.",
            paymentDelayedTitle: "Confirmation pending",
            paymentDelayedText: "Papi has not confirmed the result yet. This is not treated as a failure; you can check again.",
            paymentSuccessTitle: "Payment successful!",
            paymentSuccessText: "The payment is confirmed and the Kajikajy subscription is active.",
            paymentFailureTitle: "Payment failed",
            paymentFailureText: "The server confirmed that the payment did not complete. You can try again.",
            paymentPopupBlockedTitle: "Payment window blocked",
            paymentPopupBlockedText: "Your browser blocked the Papi window. Allow pop-ups for Kajikajy, then try again.",
            reopenPaymentButton: "Reopen payment",
            continueSameTabButton: "Continue in this tab",
            paymentStorageUnavailableText: "Session storage is unavailable. To keep secure payment tracking, retry in the Papi window.",
            checkAgainButton: "Check now",
            goToAccountButton: "Go to my account",
            paymentProviderLabel: "Provider",
            paymentUnavailableText: "Payment is not available in this environment.",
            retryButton: "Try again",
            closeButton: "Close",
            validationBusiness: "Enter the business to attach.",
            validationEmail: "Enter a valid email.",
            validationPhone: "Enter a valid Mobile Money number.",
            validationPin: "Create a 4-digit PIN.",
            validationPinMismatch: "The PIN entries do not match.",
            firebaseMissing: "Firebase web configuration is missing.",
            invoiceError: "The invoice could not be prepared.",
            continueButton: "Pay",
        },
        account: {
            eyebrow: "My account",
            title: "Check your Kajikajy subscription",
            lead: "The active plan, expiration date and renewal actions will appear here once access is activated.",
            signinTitle: "Secure sign-in",
            signinText: "Access will open with the final subscription flow. Actions remain locked until payment is active.",
            signinButton: "Sign in",
            statusGridLabel: "Subscription overview",
            statusTitle: "Status",
            statusText: "Subscription state at a glance",
            plansTitle: "Plans",
            renewalTitle: "Renewal",
            renewalText: "Renewal and upgrade actions planned from this space",
        },
    },
};
//# sourceMappingURL=web-i18n.js.map